/**
 * ProviderInspectionPage - Vistoria aberta dentro do aplicativo do prestador.
 *
 * Reaproveita integralmente a página oficial de vistoria externa (`PublicInspectionPage`):
 * perguntas, fotos, antifraude, aceite e envio continuam na mesma estrutura.
 * A sessão é emitida pelo servidor (`checklist.open` na Edge Function `provider-portal`)
 * e gravada na mesma chave lida por `usePublicChecklist`, dispensando nova confirmação de CPF/CNPJ.
 *
 * Rolagem: segue o mesmo padrão do `ProviderPortalShell` — o documento fica travado
 * (`provider-portal-open` no body) e somente a área central rola, evitando o efeito
 * "elástico" com faixas brancas no celular.
 *
 * RELEASE: 2026-10-13 v1.1.0 - Rolagem estável: documento travado e área central rolável.
 * RELEASE: 2026-10-13 v1.0.0 - Criação inicial.
 */
import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PublicInspectionPage from '@/pages/public/PublicInspectionPage';
import { providerInspectionReturnPath } from '@/lib/providerPortalNavigation';

/** Chave de armazenamento da sessão — idêntica à usada em `usePublicChecklist`. */
export const providerInspectionSessionKey = (token: string) => `uaizy-vistoria-${token}`;

export default function ProviderInspectionPage() {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();

  // Trava a rolagem do documento enquanto a vistoria estiver aberta (mesmo padrão do shell do app).
  useEffect(() => {
    document.body.classList.add('provider-portal-open');
    return () => document.body.classList.remove('provider-portal-open');
  }, []);

  // Retorno explícito: o histórico do navegador não identifica a OS de origem.
  const back = () => {
    void queryClient.invalidateQueries({ queryKey: ['pp', tenantSlug] });
    navigate(providerInspectionReturnPath(tenantSlug, params), { replace: true });
  };
  return (
    <div className="provider-app flex h-[100dvh] flex-col overflow-hidden bg-background">
      <div className="flex shrink-0 items-center gap-2 bg-sidebar px-3 py-2 text-sidebar-foreground">
        <Button variant="ghost" size="sm" className="text-sidebar-foreground hover:bg-sidebar-accent" onClick={back}>
          <ArrowLeft className="mr-1 h-4 w-4" />Voltar para a OS
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <PublicInspectionPage />
      </div>
    </div>
  );
}
